import { useTranslate } from '@tolgee/react';
import { Alert, Tree } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';

import ArrowRightSvg from '../../../assets/icons/ArrowRightSvg';
import FolderSvg from '../../../assets/icons/FolderSvg';
import theme from '../../../assets/theme';
import useDuplicateTemplate from '../../../hooks/useDuplicateTemplate';
import useFolders from '../../../hooks/useFolders';
import useWorkSpace from '../../../hooks/useWorkSpace';
import useWorkspaceFolders from '../../../hooks/useWorkspaceFolders';
import { ById } from '../../../services/backendService/types';
import { getExpandedTreeNodes } from '../../../utils/blueprintUtils';
import { getTitle } from '../../../utils/templateUtils';
import { Button } from '../../_atoms/Button';
import { IconWrapper } from '../../_atoms/IconWrapper';
import BaseModal from '../../_molecules/BaseModal';
import { Slide } from '../../SlideComponents/slide.types';

export type DuplicateLessonData = {
  parentId: string | null;
  isPrivate: boolean;
  id: string;
  title?: string | null;
  slides?: Slide[] | null;
};

const DuplicateLessonToFolder = ({
  data,
  onDone,
  onCancel,
}: {
  data: DuplicateLessonData;
  onDone: () => void;
  onCancel: () => void;
}) => {
  const {
    duplicateTemplate: duplicate,
    isDuplicatingTemplate: isDuplicating,
  } = useDuplicateTemplate();

  const { allFolders: folders } = useFolders();
  const { allFolders: workspaceFolders } = useWorkspaceFolders();

  const [selectedNode, setSelectedNode] = useState<{
    key: string;
    isPrivate: boolean;
  } | null>({
    key: 'root',
    isPrivate: true,
  });

  const { workspace } = useWorkSpace();
  const [expandedKeys, setExpandedKeys] = useState<string[]>();
  const [expandedWorkspaceKeys, setExpandedWorkspaceKeys] = useState<string[]>();
  const { t } = useTranslate();
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleSelectedNode = (s: any[], isPrivate: boolean) => {
    const selected = s[0];
    setSelectedNode({
      key: selected ? selected.toString() : data.parentId ? data.parentId : 'root',
      isPrivate: selected ? isPrivate : data.isPrivate,
    });
  };
  useEffect(() => {
    if (data) {
      if (data && data.parentId) {
        setSelectedNode({ key: data.parentId, isPrivate: data.isPrivate });
      } else {
        setSelectedNode({ key: 'root', isPrivate: true });
      }
    }
  }, [data]);

  //private tree
  useEffect(() => {
    if (folders && data && data.isPrivate) {
      if (data.parentId) {
        setExpandedKeys(['root', ...getExpandedTreeNodes(folders, data.parentId)]);
      } else if (data && !data.parentId) {
        setExpandedKeys([
          'root',
          ...folders.filter(folder => !folder.parentId).map(folder => folder.id),
        ]);
      }
    }
  }, [folders, data]);

  // Workspace tree
  useEffect(() => {
    if (
      workspace &&
      workspaceFolders &&
      data &&
      data.isPrivate === false &&
      data.parentId
    ) {
      setExpandedWorkspaceKeys([
        data.parentId,
        ...getExpandedTreeNodes(workspaceFolders, data.parentId, workspace.id),
      ]);
    }
  }, [data, workspace, workspaceFolders]);

  // My lesson data tree
  const { dataTree, entityMap } = useMemo(() => {
    if (folders) {
      const icon = (
        <IconWrapper
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          height="16px"
          width="16px"
        >
          <FolderSvg />
        </IconWrapper>
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const entityMap: ById<any> = {
        root: {
          key: 'root',
          title: t('My lessons', 'My lessons'),
          parentId: null,
          icon,
          children: [],
        },
      };
      folders.forEach(folder => {
        entityMap[folder.id] = {
          key: folder.id,
          title: folder.title,
          parentId: folder.parentId,
          icon,
          children: [],
        };
      });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const dataTree: any[] = [entityMap['root']];
      folders.forEach(folder => {
        if (!!folder.parentId) {
          if (folder.parentId === data.id || folder.id === data.id) {
            // hide subfolders within the current selected folder/template
            return;
          }
          entityMap[folder.parentId].children.push(entityMap[folder.id]);
        } else {
          if (folder.id === data.id) {
            // hide subfolders within the current selected folder/template
            return;
          }
          dataTree[0].children.push(entityMap[folder.id]);
        }
      });
      return { dataTree, entityMap };
    }
    return { dataTree: undefined, entityMap: undefined };
  }, [folders, t, data.id]);

  // Workspace expanded tree
  const { workspaceDataTree, workspaceEntityMap } = useMemo(() => {
    if (workspaceFolders && workspace) {
      const icon = (
        <IconWrapper
          style={{
            height: '100%',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
          height="16px"
          width="16px"
        >
          <FolderSvg />
        </IconWrapper>
      );
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const workspaceEntityMap: ById<any> = {};
      (workspaceEntityMap[workspace.id] = {
        key: workspace.id,
        title: workspace.title,
        parentId: null,
        icon,
        children: [],
      }),
        workspaceFolders.forEach(folder => {
          workspaceEntityMap[folder.id] = {
            key: folder.id,
            title: folder.title,
            parentId: folder.parentId,
            icon,
            children: [],
          };
        });
      // eslint-disable-next-line @typescript-eslint/no-explicit-any
      const workspaceDataTree: any[] = [workspaceEntityMap[workspace.id]];

      workspaceFolders.forEach(workspaceFolder => {
        if (workspaceFolder.parentId) {
          if (
            workspaceFolder.parentId === workspace.id &&
            workspaceDataTree?.[0] &&
            workspaceDataTree?.[0].children
          ) {
            // If the workspaceFolder's parentId is the same as the workspace.id then add it to the root
            workspaceDataTree?.[0].children.push(workspaceEntityMap[workspaceFolder.id]);
          } else {
            // If there is a parentId but it's not the workspace.id then add it to the correct parent folder
            if (
              workspaceEntityMap?.[workspaceFolder.parentId] &&
              workspaceEntityMap?.[workspaceFolder.id]
            ) {
              workspaceEntityMap[workspaceFolder.parentId].children.push(
                workspaceEntityMap[workspaceFolder.id],
              );
            }
          }
        }
      });
      return { workspaceDataTree, workspaceEntityMap };
    }
    return { workspaceDataTree: undefined, workspaceEntityMap: undefined };
  }, [workspaceFolders, workspace]);

  const handleDuplicateAndMove = (
    selectedNode: {
      key: string;
      isPrivate: boolean;
    } | null,
  ) => {
    if (selectedNode) {
      duplicate({
        templateId: data.id,
        isPrivate: selectedNode.isPrivate,
        parentId: selectedNode.key === 'root' ? null : selectedNode.key,
        onSuccessFunction: () => {
          onDone();
        },
      });

      return;
    }
  };

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  const handleExpand = (keys: any[], isPrivate: boolean) => {
    if (isPrivate) {
      setExpandedKeys(keys);
    } else {
      setExpandedWorkspaceKeys(keys);
    }
  };

  if (!entityMap) return null;
  return (
    <BaseModal
      title={`Duplicate ${
        !!data.title
          ? `"${getTitle({
              title: data.title,
              slides: data.slides ? data.slides : [],
              maxLength: 75,
            })}"`
          : ''
      } to...`}
      open
      onCancel={onCancel}
    >
      {workspace && (
        <TreeContainer>
          <Tree
            onSelect={s => {
              handleSelectedNode(s, false);
            }}
            selectedKeys={selectedNode?.key ? [selectedNode?.key] : undefined}
            expandedKeys={expandedWorkspaceKeys}
            onExpand={s => handleExpand(s, false)}
            showIcon
            switcherIcon={
              <IconWrapper style={{ transform: 'rotate(90deg)' }}>
                <ArrowRightSvg />
              </IconWrapper>
            }
            treeData={workspaceDataTree}
          />
        </TreeContainer>
      )}
      <TreeContainer>
        <Tree
          onSelect={s => {
            handleSelectedNode(s, true);
          }}
          selectedKeys={selectedNode?.key ? [selectedNode?.key] : undefined}
          expandedKeys={expandedKeys}
          onExpand={s => handleExpand(s, true)}
          showIcon
          switcherIcon={
            <IconWrapper style={{ transform: 'rotate(90deg)' }}>
              <ArrowRightSvg />
            </IconWrapper>
          }
          treeData={dataTree}
        />
      </TreeContainer>
      {selectedNode && data.isPrivate && !selectedNode?.isPrivate && workspaceEntityMap && (
        <Alert
          message={
            <h3 style={{ color: 'white', fontWeight: 'bold' }}>{t('Are you sure?')}</h3>
          }
          style={{
            backgroundColor: theme.colors.red,
            color: theme.colors.white,
            fontWeight: 'bold',
          }}
          description={t('movingToFolderWarning1')}
          type="error"
          showIcon={false}
          closable={false}
        />
      )}
      {selectedNode && !data.isPrivate && selectedNode?.isPrivate === true && entityMap && (
        <Alert
          message={<h3 style={{ color: 'white', fontWeight: 'bold' }}>Are you sure?</h3>}
          style={{
            backgroundColor: theme.colors.red,
            color: theme.colors.white,
            fontWeight: 'bold',
          }}
          description={t('moveingToFolderWarning2')}
          type="error"
          showIcon={false}
          closable={false}
        />
      )}
      <div
        style={{
          display: 'flex',
          alignItems: 'center',
          width: '100%',
          flexWrap: 'wrap',
          marginTop: '20px',
        }}
      >
        <Button
          width="100%"
          type="primary"
          loading={isDuplicating}
          onClick={() => {
            handleDuplicateAndMove(selectedNode);
          }}
        >
          {selectedNode?.isPrivate && (
            <>
              {selectedNode &&
              (selectedNode.key === data.parentId ||
                (selectedNode.key === 'root' && !data.parentId))
                ? 'Duplicate'
                : `Duplicate to "${
                    selectedNode ? entityMap[selectedNode.key].title : ''
                  }"`}
            </>
          )}

          {selectedNode?.isPrivate == false && workspaceEntityMap && (
            <>
              {selectedNode && selectedNode.key === data.parentId
                ? 'Duplicate'
                : `Duplicate to "${
                    workspaceEntityMap ? workspaceEntityMap[selectedNode?.key].title : ''
                  }"`}
            </>
          )}
        </Button>
      </div>
    </BaseModal>
  );
};

export default DuplicateLessonToFolder;

const TreeContainer = styled.div`
  width: 100%;
  padding: 10px 0px;
  .ant-tree {
    background: transparent;
  }
  .ant-tree-title {
    font-weight: bold;
  }
  &&&& .ant-tree-node-selected {
    color: white;
    background-color: ${theme.colors.green};
  }

  && .ant-tree-node-content-wrapper {
    border-radius: 5px;
  }
`;
